'use client';

import React, { useState, createContext, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';

const USER_CONSENT_COOKIE_NAME = 'user_consent';
const COOKIE_RANGE_COOKIE_NAME = 'cookie_range'; // none, all, a:b:c ,a\b\c 是cookie范围

// 定义上下文类型
interface CookieContextType {
    showAccept: () => void;
    hideAccept: () => void;
    getAcceptStatus: () => boolean;
    showCookieSettings: () => void;
    hideCookieSettings: () => void;
    getCookieSettingsStatus: () => boolean;

    // 创建用户同意
    createUserConsent: (isAgree: 'agree' | 'reject') => void;

    // 设置cookie范围
    updateCookiesRange: (range: string) => void;

    getCookieRange: () => string;
}

const CookieContext = createContext<CookieContextType>({
    showAccept: () => {},
    hideAccept: () => {},
    getAcceptStatus: () => false,
    showCookieSettings: () => {},
    hideCookieSettings: () => {},
    getCookieSettingsStatus: () => false,

    createUserConsent: (consent: string) => {},
    updateCookiesRange: () => {},
    getCookieRange: () => '',
});

export function CookieContextProvider({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) {
    const [acceptIsOpen, setAcceptIsOpen] = useState(false);
    const [settingIsOpen, setSettingIsOpen] = useState(false);

    useEffect(() => {
        if (
            getUserConsent() === undefined ||
            getCookieRange() === '' ||
            getUserConsent() === 'reject'
        ) {
            setAcceptIsOpen(true);
        }
    }, []);

    const showAccept = () => {
        setAcceptIsOpen(true);
    };

    const hideAccept = () => {
        setAcceptIsOpen(false);
    };

    const getAcceptStatus = () => {
        return acceptIsOpen;
    };

    const showCookieSettings = () => {
        setSettingIsOpen(true);
    };

    const hideCookieSettings = () => {
        setSettingIsOpen(false);
    };

    const getCookieSettingsStatus = () => {
        return settingIsOpen;
    };

    function createUserConsent(isAgree: 'agree' | 'reject') {
        Cookies.set(USER_CONSENT_COOKIE_NAME, isAgree, {
            expires: 60 * 60 * 24 * 365,

            // This is a secure cookie
            // secure: true,
            // httpOnly: true,
            // sameSite: 'strict',
        });
    }

    function getUserConsent() {
        return Cookies.get(USER_CONSENT_COOKIE_NAME);
    }

    function getCookieRange() {
        return Cookies.get(COOKIE_RANGE_COOKIE_NAME) ?? '';
    }

    function updateCookiesRange(range: string) {
        Cookies.set(COOKIE_RANGE_COOKIE_NAME, range, {
            expires: 60 * 60 * 24 * 365,
        });
    }

    return (
        <CookieContext.Provider
            value={{
                showAccept,
                hideAccept,
                getAcceptStatus,
                showCookieSettings,
                hideCookieSettings,
                getCookieSettingsStatus,
                createUserConsent,
                updateCookiesRange,
                getCookieRange,
            }}
        >
            {children}
        </CookieContext.Provider>
    );
}

export default function useCookieContext() {
    return useContext(CookieContext);
}
